import { createContext, useContext } from 'react';

/** Where a merchant changes the store's ERP rules, in the Magento admin. */
export const storeSettingsPath = 'Stores ▸ Configuration ▸ HestaWorks ▸ Commerce Ops ▸ ERP integration';

/** Where the connection code is pasted in the Magento admin (CO-82). */
export const storeConnectionPath = 'Stores ▸ Configuration ▸ HestaWorks ▸ Commerce Ops ▸ Connection';

/**
 * StoreCopy is every sentence of the ERP screens that names the store's own
 * admin: where its settings are, what the extension is called, which button
 * saves the code. A Magento store runs the Commerce Ops modules; a
 * WooCommerce store runs the Commerce Ops for WooCommerce plugin (CO-137),
 * and its screens must not speak Magento.
 */
export interface StoreCopy {
  platform: 'magento2' | 'woocommerce';
  /** Where the merchant works, e.g. "the Magento admin". */
  admin: string;
  /** Short name of that place, e.g. "Magento". */
  adminShort: string;
  settingsPath: string;
  connectionPath: string;
  installTitle: string;
  installHelp: string;
  /** Whether Commerce Ops offers the extension as a download. */
  downloadable: boolean;
  /** The store's button that saves a pasted code. */
  saveButton: string;
  /** The store's button that checks the connection at once. */
  checkButton: string;
  /** Why a store only checks in when asked. */
  schedulerProblem: string;
  /** What holds the ERP article code: an attribute in Magento, a field in WooCommerce. */
  matchField: string;
  /** The products that hold stock and take it from the ERP. */
  stockProducts: string;
  /** Where an ERP warehouse's stock goes in the store. */
  stockLocation: string;
  /** How a product created from an ERP article starts: never on sale. */
  createdAs: string;
}

export const magentoStoreCopy: StoreCopy = {
  platform: 'magento2',
  admin: 'the Magento admin',
  adminShort: 'Magento',
  settingsPath: storeSettingsPath,
  connectionPath: storeConnectionPath,
  installTitle: 'Install the Commerce Ops extension in Magento',
  installHelp:
    'The CommerceOps_Core and CommerceOps_Erp modules, version 1.3 or later; run setup:upgrade after adding them. An older version asks for a signing key instead of a connection code: update it first. Magento cron must be running.',
  downloadable: false,
  saveButton: 'Save Config',
  checkButton: 'Check connection now',
  schedulerProblem: 'Magento cron is not running, so the store only checks when asked.',
  matchField: 'attribute',
  stockProducts: 'simple and virtual products',
  stockLocation: 'inventory source',
  createdAs: 'disabled',
};

export const wooCommerceStoreCopy: StoreCopy = {
  platform: 'woocommerce',
  admin: 'the WordPress admin',
  adminShort: 'WordPress',
  settingsPath: 'HestaWorks ▸ Commerce Ops ▸ ERP',
  connectionPath: 'HestaWorks ▸ Commerce Ops',
  installTitle: 'Install the Commerce Ops for WooCommerce plugin',
  installHelp:
    'Download it below, then in WordPress go to Plugins ▸ Add New ▸ Upload Plugin and activate it. It needs WooCommerce 8.5 or newer and PHP 8.1 or newer. Later versions are offered on WordPress’s Updates screen.',
  downloadable: true,
  saveButton: 'Connect',
  checkButton: 'Check connection',
  schedulerProblem:
    'WordPress runs background tasks only when the site gets visits, so a quiet store checks in late. A system cron for WordPress fixes this.',
  matchField: 'field',
  stockProducts: 'products and variations',
  stockLocation: 'stock',
  createdAs: 'as drafts',
};

/** storeCopyFor picks the words for a store installation's platform. */
export function storeCopyFor(platform: string | undefined): StoreCopy {
  return platform === 'woocommerce' ? wooCommerceStoreCopy : magentoStoreCopy;
}

/** The ERP section sets the project's store words; Magento's are the default. */
export const StoreCopyContext = createContext<StoreCopy>(magentoStoreCopy);

export function useStoreCopy(): StoreCopy {
  return useContext(StoreCopyContext);
}
